/**
 * One set of field rules for the apps and the API.
 *
 * The browser checks these so people are told what's wrong while they type;
 * the API checks the same rules again, because a request can arrive from
 * anywhere. Each `*Error` function returns a message to show, or null when the
 * value is fine — so a form reads `const err = phoneError(value)`.
 *
 * Phone numbers are Indian mobiles. They are ACCEPTED in any of the shapes
 * people actually type ("98765 43210", "+91 98765-43210", "098765 43210") and
 * STORED in one shape, E.164 (+919876543210), which is what the existing rows,
 * the SMS gateways and WhatsApp all use.
 */

// --- Phone ------------------------------------------------------------------

export const PHONE_HINT = '10-digit mobile number, e.g. 98765 43210';
/** Indian mobiles start 6–9; landlines and short codes can't receive OTP or WhatsApp. */
const TEN_DIGIT = /^[6-9]\d{9}$/;

/** The 10 significant digits of a number typed in any usual shape, or null. */
export function phoneDigits(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  const local = digits.length > 10 && digits.startsWith('91')
    ? digits.slice(-10)
    : digits.length === 11 && digits.startsWith('0')
      ? digits.slice(1)
      : digits;
  return TEN_DIGIT.test(local) ? local : null;
}

/** Storage shape: +919876543210, or null when it isn't a valid mobile. */
export function normalisePhone(value) {
  const local = phoneDigits(value);
  return local ? `+91${local}` : null;
}

/** Display shape: 98765 43210 (falls back to whatever was stored). */
export function formatPhone(value) {
  const local = phoneDigits(value);
  return local ? `${local.slice(0, 5)} ${local.slice(5)}` : (value ?? '');
}

export function isValidPhone(value) {
  return phoneDigits(value) !== null;
}

/**
 * @param value what the person typed
 * @param required true when the field can't be left blank
 */
export function phoneError(value, { required = true, label = 'phone number' } = {}) {
  const raw = String(value ?? '').trim();
  if (!raw) return required ? `Enter your ${label}` : null;
  const digits = raw.replace(/\D/g, '');
  if (/[a-zA-Z]/.test(raw)) return 'A phone number can only contain digits';
  const local = phoneDigits(raw);
  if (local) return null;
  if (digits.length < 10) return `That's too short — enter all 10 digits`;
  if (digits.length > 12) return `That's too long — enter a 10-digit mobile number`;
  return 'Enter a valid Indian mobile number (starting 6, 7, 8 or 9)';
}

// --- Email ------------------------------------------------------------------

// Deliberately simple: one @, something either side, a dot in the domain.
// Anything stricter rejects addresses that really do work.
const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export function isValidEmail(value) {
  const v = String(value ?? '').trim();
  return v.length <= 200 && EMAIL.test(v);
}

export function emailError(value, { required = true } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? 'Enter your email address' : null;
  if (v.length > 200) return 'That email address is too long';
  if (!EMAIL.test(v)) return 'Enter a valid email address, like you@example.com';
  return null;
}

// --- Password ---------------------------------------------------------------

export const PASSWORD_MIN = 8;
export const PASSWORD_HINT = `At least ${PASSWORD_MIN} characters, with a letter and a number`;

export function passwordError(value, { required = true } = {}) {
  const v = String(value ?? '');
  if (!v) return required ? 'Choose a password' : null;
  if (v.length < PASSWORD_MIN) return `Use at least ${PASSWORD_MIN} characters`;
  if (v.length > 200) return 'That password is too long';
  if (!/[a-zA-Z]/.test(v) || !/\d/.test(v)) return 'Include at least one letter and one number';
  return null;
}

// --- Name, text -------------------------------------------------------------

export function nameError(value, { required = true, label = 'name', min = 2, max = 120 } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? `Enter your ${label}` : null;
  if (v.length < min) return `That ${label} looks too short`;
  if (v.length > max) return `That ${label} is too long`;
  if (!/[a-zA-Zऀ-ॿ]/.test(v)) return `Enter a real ${label}`;
  return null;
}

export function requiredError(value, label = 'this') {
  const v = typeof value === 'string' ? value.trim() : value;
  if (v === '' || v === null || v === undefined) return `Enter ${label}`;
  return null;
}

// --- Address ----------------------------------------------------------------

const PINCODE = /^[1-9]\d{5}$/;

export function pincodeError(value, { required = true } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? 'Enter the PIN code' : null;
  if (!PINCODE.test(v)) return 'Enter a 6-digit PIN code';
  return null;
}

export function addressLineError(value, { required = true, label = 'address', min = 4, max = 200 } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? `Enter the ${label}` : null;
  if (v.length < min) return `That ${label} looks too short`;
  if (v.length > max) return `That ${label} is too long`;
  return null;
}

// --- Payout details ---------------------------------------------------------

const IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT = /^\d{6,18}$/;
// handle@bank — what UPI apps accept.
const UPI = /^[a-zA-Z0-9.\-_]{2,64}@[a-zA-Z][a-zA-Z0-9.\-_]{1,63}$/;

export function ifscError(value, { required = true } = {}) {
  const v = String(value ?? '').trim().toUpperCase();
  if (!v) return required ? 'Enter the IFSC code' : null;
  if (!IFSC.test(v)) return 'Enter a valid IFSC code, like HDFC0001234';
  return null;
}

export function accountNumberError(value, { required = true } = {}) {
  const v = String(value ?? '').replace(/\s/g, '');
  if (!v) return required ? 'Enter the account number' : null;
  if (!ACCOUNT.test(v)) return 'An account number is 6 to 18 digits';
  return null;
}

export function upiIdError(value, { required = true } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return required ? 'Enter the UPI ID' : null;
  if (!UPI.test(v)) return 'Enter a valid UPI ID, like name@bank';
  return null;
}

/** First error from a { field: message|null } map, for "fix this first" summaries. */
export function firstError(errors) {
  return Object.values(errors ?? {}).find(Boolean) ?? null;
}
